import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import api from '../lib/api';
import { Plus, Trash2, Loader2, ChevronDown, ChevronRight, ListTodo, UserCircle, Calendar, X, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const SubtaskList = ({ taskId, subtasks = [], onUpdate, collapsibleOnMobile = false, members = [], highlightUserId = null }) => {
  const hasHighlightedSubtask = highlightUserId && subtasks.some(st => st.assigned_to === highlightUserId);
  const [isExpanded, setIsExpanded] = useState(hasHighlightedSubtask);
  const [isAdding, setIsAdding] = useState(false);
  const [newSubtask, setNewSubtask] = useState('');
  const [loading, setLoading] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');

  const completedCount = subtasks.filter(st => st.completed).length;
  const totalCount = subtasks.length;

  const handleAddSubtask = async () => {
    if (!newSubtask.trim()) return;
    
    setLoading(true);
    try {
      const response = await api.post(`/tasks/${taskId}/subtasks`, {
        title: newSubtask.trim()
      });
      onUpdate?.([...subtasks, response.data]);
      setNewSubtask('');
      setIsAdding(false);
      toast.success('Subtask added');
    } catch (error) {
      toast.error('Failed to add subtask');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleComplete = async (subtaskId, currentStatus) => {
    setUpdatingId(subtaskId);
    try {
      await api.patch(`/tasks/${taskId}/subtasks/${subtaskId}`, {
        completed: !currentStatus
      });
      const updated = subtasks.map(st => 
        st.subtask_id === subtaskId ? { ...st, completed: !currentStatus } : st
      );
      onUpdate?.(updated);
    } catch (error) {
      toast.error('Failed to update subtask');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleAssignSubtask = async (subtaskId, userId, userName) => {
    setUpdatingId(subtaskId);
    try {
      await api.patch(`/tasks/${taskId}/subtasks/${subtaskId}`, {
        assigned_to: userId || ""
      });
      const updated = subtasks.map(st =>
        st.subtask_id === subtaskId
          ? { ...st, assigned_to: userId || null, assigned_to_name: userName || null }
          : st
      );
      onUpdate?.(updated);
      toast.success(userId ? `Assigned to ${userName}` : 'Unassigned');
    } catch (error) {
      toast.error('Failed to assign subtask');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleEditSubtask = async (subtaskId) => {
    const trimmed = editTitle.trim();
    if (!trimmed) { setEditingId(null); return; }
    const current = subtasks.find(st => st.subtask_id === subtaskId);
    if (current && current.title === trimmed) { setEditingId(null); return; }
    setUpdatingId(subtaskId);
    try {
      await api.patch(`/tasks/${taskId}/subtasks/${subtaskId}`, { title: trimmed });
      const updated = subtasks.map(st =>
        st.subtask_id === subtaskId ? { ...st, title: trimmed } : st
      );
      onUpdate?.(updated);
      toast.success('Subtask updated');
    } catch (error) {
      toast.error('Failed to update subtask');
    } finally {
      setUpdatingId(null);
      setEditingId(null);
    }
  };

  const handleDeleteSubtask = async (subtaskId) => {
    setUpdatingId(subtaskId);
    try {
      await api.delete(`/tasks/${taskId}/subtasks/${subtaskId}`);
      const updated = subtasks.filter(st => st.subtask_id !== subtaskId);
      onUpdate?.(updated);
      toast.success('Subtask deleted');
    } catch (error) {
      toast.error('Failed to delete subtask');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleSetDueDate = async (subtaskId, dateStr) => {
    setUpdatingId(subtaskId);
    try {
      await api.patch(`/tasks/${taskId}/subtasks/${subtaskId}`, {
        due_date: dateStr || ""
      });
      const updated = subtasks.map(st =>
        st.subtask_id === subtaskId ? { ...st, due_date: dateStr || null } : st
      );
      onUpdate?.(updated);
    } catch (error) {
      toast.error('Failed to update due date');
    } finally {
      setUpdatingId(null);
    }
  };

  const formatShortDate = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr + 'T00:00:00');
    const today = new Date(); today.setHours(0,0,0,0);
    const diff = Math.floor((d - today) / 86400000);
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Tmrw';
    if (diff < 0) return d.toLocaleDateString('en', { month: 'short', day: 'numeric' });
    return d.toLocaleDateString('en', { month: 'short', day: 'numeric' });
  };

  const getDateColor = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    const today = new Date(); today.setHours(0,0,0,0);
    const diff = Math.floor((d - today) / 86400000);
    if (diff < 0) return 'text-red-500';
    if (diff === 0) return 'text-orange-500';
    if (diff <= 2) return 'text-amber-500';
    return 'text-muted-foreground';
  };

  return (
    <div className="mt-2">
      {/* Subtask toggle header - compact on mobile */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 ${collapsibleOnMobile ? 'md:gap-1' : ''}`}
        data-testid={`subtask-toggle-${taskId}`}
      >
        {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        {collapsibleOnMobile ? (
          <>
            {/* Mobile compact view */}
            <span className="md:hidden flex items-center gap-1">
              <ListTodo className="w-3 h-3" />
              {totalCount > 0 ? `${completedCount}/${totalCount}` : '+'}
            </span>
            {/* Desktop full view */}
            <span className="hidden md:inline">
              {totalCount > 0 ? `${completedCount}/${totalCount} subtasks` : 'Add subtasks'}
            </span>
          </>
        ) : (
          <span>
            {totalCount > 0 ? `${completedCount}/${totalCount} subtasks` : 'Add subtasks'}
          </span>
        )}
        {totalCount > 0 && completedCount === totalCount && (
          <span className="text-green-500 ml-1">&#10003;</span>
        )}
      </button>

      {/* Expanded subtask list */}
      {isExpanded && (
        <div className="mt-2 ml-4 space-y-1">
          {/* Existing subtasks — sorted by due date (earliest first, no-date last) */}
          {[...subtasks].sort((a, b) => {
            if (!a.due_date && !b.due_date) return 0;
            if (!a.due_date) return 1;
            if (!b.due_date) return -1;
            return a.due_date.localeCompare(b.due_date);
          }).map(subtask => (
            <div 
              key={subtask.subtask_id} 
              className={`flex items-center gap-2 group py-1 px-2 -mx-2 rounded-md transition-colors hover:bg-sky-50 dark:hover:bg-slate-600/60 ${highlightUserId && subtask.assigned_to === highlightUserId ? 'bg-[#4A9CC7]/5 dark:bg-[#4A9CC7]/10' : ''}`}
              data-testid={`subtask-${subtask.subtask_id}`}
            >
              {updatingId === subtask.subtask_id ? (
                <Loader2 className="w-3 h-3 animate-spin text-gray-400 shrink-0" />
              ) : (
                <Checkbox
                  checked={subtask.completed}
                  onCheckedChange={() => handleToggleComplete(subtask.subtask_id, subtask.completed)}
                  className="w-3.5 h-3.5 shrink-0 data-[state=checked]:bg-[#4A9CC7]"
                />
              )}
              {editingId === subtask.subtask_id ? (
                <Input
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleEditSubtask(subtask.subtask_id);
                    if (e.key === 'Escape') setEditingId(null);
                  }}
                  onBlur={() => handleEditSubtask(subtask.subtask_id)}
                  className="h-6 text-xs max-w-[45%] px-1"
                  autoFocus
                  data-testid={`subtask-edit-input-${subtask.subtask_id}`}
                />
              ) : (
                <span
                  className={`text-xs shrink-0 max-w-[45%] truncate cursor-pointer ${subtask.completed ? 'line-through text-gray-400 dark:text-gray-500' : 'text-gray-600 dark:text-gray-300'}`}
                  onDoubleClick={() => { setEditingId(subtask.subtask_id); setEditTitle(subtask.title); }}
                  title="Double-click to edit"
                  data-testid={`subtask-title-${subtask.subtask_id}`}
                >
                  {subtask.title}
                </span>
              )}

              {/* Action cluster — sits right after title */}
              <div className="flex items-center gap-1 shrink-0">
                {/* Inline assignee selector */}
                {members.length > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className={`flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full transition-colors ${
                          subtask.assigned_to
                            ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300'
                            : 'text-gray-400 opacity-0 group-hover:opacity-100 hover:bg-gray-200 dark:hover:bg-slate-600'
                        }`}
                        data-testid={`subtask-assign-${subtask.subtask_id}`}
                      >
                        <UserCircle className="w-3 h-3" />
                        <span className="max-w-[60px] truncate hidden md:inline">
                          {subtask.assigned_to_name || 'Assign'}
                        </span>
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-44 max-h-48 overflow-y-auto">
                      {subtask.assigned_to && (
                        <DropdownMenuItem
                          onClick={() => handleAssignSubtask(subtask.subtask_id, null, null)}
                          className="text-xs text-red-500"
                          data-testid={`subtask-unassign-${subtask.subtask_id}`}
                        >
                          Unassign
                        </DropdownMenuItem>
                      )}
                      {members.map(m => (
                        <DropdownMenuItem
                          key={m.user_id}
                          onClick={() => handleAssignSubtask(subtask.subtask_id, m.user_id, m.name || m.email)}
                          className={`text-xs ${subtask.assigned_to === m.user_id ? 'bg-purple-50 dark:bg-purple-900/30 font-medium' : ''}`}
                          data-testid={`subtask-assign-member-${m.user_id}`}
                        >
                          <UserCircle className="w-3 h-3 mr-2 flex-shrink-0" />
                          <span className="truncate">{m.name || m.email}</span>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}

                {/* Inline date selector */}
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      className={`flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full transition-colors ${
                        subtask.due_date
                          ? `${getDateColor(subtask.due_date)} bg-gray-100 dark:bg-slate-700`
                          : 'text-gray-400 opacity-0 group-hover:opacity-100 hover:bg-gray-200 dark:hover:bg-slate-600'
                      }`}
                      data-testid={`subtask-date-${subtask.subtask_id}`}
                    >
                      <Calendar className="w-3 h-3" />
                      <span className="hidden md:inline max-w-[55px] truncate">{formatShortDate(subtask.due_date) || 'Date'}</span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-2" align="start">
                    <div className="space-y-1">
                      <input
                        type="date"
                        value={subtask.due_date || ''}
                        onChange={(e) => handleSetDueDate(subtask.subtask_id, e.target.value)}
                        className="text-xs border rounded px-2 py-1 w-full dark:bg-slate-800 dark:border-slate-600"
                        data-testid={`subtask-date-input-${subtask.subtask_id}`}
                      />
                      {subtask.due_date && (
                        <button
                          onClick={() => handleSetDueDate(subtask.subtask_id, "")}
                          className="text-[10px] text-red-500 hover:underline flex items-center gap-1"
                          data-testid={`subtask-date-clear-${subtask.subtask_id}`}
                        >
                          <X className="w-2.5 h-2.5" />Clear date
                        </button>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>

                {/* Edit */}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100 text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                  onClick={() => { setEditingId(subtask.subtask_id); setEditTitle(subtask.title); }}
                  data-testid={`subtask-edit-${subtask.subtask_id}`}
                >
                  <Pencil className="w-3 h-3" />
                </Button>

                {/* Delete */}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                  onClick={() => handleDeleteSubtask(subtask.subtask_id)}
                  disabled={updatingId === subtask.subtask_id}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}

          {/* Add subtask input */}
          {isAdding ? (
            <div className="flex items-center gap-2">
              <Input
                value={newSubtask}
                onChange={(e) => setNewSubtask(e.target.value)}
                placeholder="Subtask title..."
                className="h-7 text-xs"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAddSubtask();
                  if (e.key === 'Escape') { setIsAdding(false); setNewSubtask(''); }
                }}
                disabled={loading}
                data-testid="new-subtask-input"
              />
              <Button
                size="sm"
                className="h-7 px-2 bg-[#4A9CC7]"
                onClick={handleAddSubtask}
                disabled={loading || !newSubtask.trim()}
              >
                {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Add'}
              </Button>
            </div>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-xs text-gray-500 hover:text-[#4A9CC7] p-0"
              onClick={() => setIsAdding(true)}
              data-testid={`add-subtask-btn-${taskId}`}
            >
              <Plus className="w-3 h-3 mr-1" />
              Add subtask
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default SubtaskList;
